import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter, HelperMessage } from '@atlaskit/form';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  ErrorComponents,
  EditCreateInvoiceGroup,
  NotificationTypes,
  successNotificationBuilder,
  useFeatureToggle,
  ContactsTooltip,
  ContactsTooltipType,
} from '@css-xp/base';
import { CommerceAddress } from '@css-xp/graphtypes';
import {
  ADDITIONAL_INVOICE_RECIPIENT,
  ADDITIONAL_INVOICE_RECIPIENTS,
  ENABLE_DESIGN_ITERATION_1,
} from '@css-xp/ppc-constants';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';

interface FormData {
  recipients: Array<string>;
}

interface Props {
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  invoiceGroupId: string;
  recipients: Array<string>;
  shipToParty: CommerceAddress;
  validDomains?: string[] | null | undefined;
}

const notificationOptions = successNotificationBuilder()
  .withId('update-billing-profile-success')
  .withHeader('Update successful')
  .withMessage('Invoice contacts updated.')
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

export const EditInvoiceContactPane = ({
  recipients,
  invoiceGroupId,
  refetch,
  validDomains,
  transactionAccountId,
  shipToParty,
}: Props): JSX.Element => {
  const [adminFieldsDirty, setAdminFieldsDirty] = React.useState<boolean>(true);
  const { closeSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceGroupRequest, any>({
    submitFormAction: updateInvoiceGroup,
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const updateAdminFieldsDirtyFlag = (flag: boolean) => {
    setAdminFieldsDirty(flag);
  };

  const handleFormSubmit = (formData: FormData) => {
    const formRecipients = (formData?.recipients ?? []).filter((recipient: string) => recipient.length > 0);

    const requestData: UpdateInvoiceGroupRequest = {
      data: {
        recipients: formRecipients,
        shipToPartyId: shipToParty.id,
      },
      invoiceGroupId,
      transactionAccountId,
    };
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  return (
    <div style={isDesignIteration1Enabled ? undefined : { maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader
              title={
                isDesignIteration1Enabled ? (
                  ''
                ) : (
                  <ContactsTooltip type={ContactsTooltipType.INVOICE} iconSize="medium">
                    <div style={isDesignIteration1Enabled ? { fontSize: '20px' } : undefined}>
                      {ADDITIONAL_INVOICE_RECIPIENTS}
                    </div>
                  </ContactsTooltip>
                )
              }
            ></FormHeader>
            {!isDesignIteration1Enabled && (
              <HelperMessage>
                Manage the {ADDITIONAL_INVOICE_RECIPIENTS.toLowerCase()} for this billing profile.
              </HelperMessage>
            )}
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the contacts for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : null}
            </FormSection>

            <FormSection>
              {isDesignIteration1Enabled ? (
                <EditCreateInvoiceGroup
                  updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                  recipients={recipients && recipients.length > 0 ? recipients : []}
                  isRequired={false}
                  label="Invoice contact email"
                  validDomains={validDomains}
                  version={2}
                  addButtonTitle="Add another invoice contact"
                />
              ) : (
                <EditCreateInvoiceGroup
                  updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                  recipients={recipients && recipients.length > 0 ? recipients : ['']}
                  isRequired={false}
                  placeholder={'Enter email address of the invoice contact'}
                  helperMessage={''}
                  title={''}
                  addButtonTitle={`Add ${ADDITIONAL_INVOICE_RECIPIENT.toLowerCase()}`}
                  validDomains={validDomains}
                />
              )}
            </FormSection>

            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance={isDesignIteration1Enabled ? undefined : 'subtle'} onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  isDisabled={!dirty && !adminFieldsDirty}
                  appearance="primary"
                  type="submit"
                  testId="update-button"
                  isLoading={isProcessing}
                >
                  {isDesignIteration1Enabled ? 'Add' : 'Save'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(EditInvoiceContactPane);
